import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getImageUrl } from "src/Common/common";
import { can, canAny, isAccountant, isClericalOver, isSuperAdmin } from "src/Common/function";
import { getActingTenant, clearActingTenant } from "src/Common/tenant";
import { termPlural } from "src/Common/vocab";
import useTicketUnreadIndicator from "src/hooks/useTicketUnreadIndicator";
import usePendingRequestCount from "src/hooks/usePendingRequestCount";
import useChatUnread from "src/hooks/useChatUnread";

// Red count badge: work orders needing attention. Caps at 99+.
const CountBadge = ({ count }) =>
  count > 0 ? (
    <span
      aria-label={`${count} work order${count === 1 ? "" : "s"} needing attention`}
      className="min-w-[20px] h-[20px] px-[6px] rounded-full bg-red-500 text-white text-[11px] leading-[20px] font-OpenSansBold text-center shrink-0"
    >
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

/**
 * SideMenu — The fixed left sidebar navigation for the application.
 *
 * Always mounted while the user is authenticated. Renders navigation items for
 * Work Orders, Purchase Orders, Recommendations, Customers, Calendar,
 * and Ticket Management (or "Report Bug" for non-Super Admin users).
 *
 * Highlights the active menu item based on the current URL pathname.
 *
 * Integrates with useTicketUnreadIndicator to display a red dot (8x8px circle)
 * to the right of the "Ticket Management" / "Report Bug" label whenever the user
 * has unread ticket activity. The red dot appears in real-time when a new ticket
 * message arrives and disappears when the user reads all unread tickets.
 *
 * Because this component is always mounted, the useTicketUnreadIndicator hook
 * maintains a persistent WebSocket subscription to the "ticket-notifications"
 * room, ensuring notifications are never missed regardless of which page the
 * user is on.
 */
function SideMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const superAdmin = isSuperAdmin();
  // A Super Admin lives in the Platform view (Tenants + Ticket Management)
  // until they enter a tenant; inside one they get the normal menu with an
  // exit control.
  const actingTenant = superAdmin ? getActingTenant() : null;
  const isPlatformMode = superAdmin && !actingTenant;
  const mainHighlightColor = "bg-blueColor";
  const bottomHighlightColor = "bg-menuHighlightColor";
  const [formsExpanded, setFormsExpanded] = useState(false);
  const [workOrdersExpanded, setWorkOrdersExpanded] = useState(false);
  const [permissionsExpanded, setPermissionsExpanded] = useState(false);
  /**
   * hasUnreadTicket: boolean from useTicketUnreadIndicator.
   * true when at least one ticket has unread messages or has never been opened.
   * Drives the red dot indicator on the ticket menu item.
   */
  const hasUnreadTicket = useTicketUnreadIndicator();
  // Customer requests awaiting approval: badge on Work Orders while its list
  // is closed, on Customer Requests while it's open.
  const pendingRequestCount = usePendingRequestCount();
  // Technicians don't see customer requests (the server leaves them out of
  // their lists), so no Customer Requests view; their unread customer
  // messages are on Jobs, which only counts their own jobs.
  const userRoles = JSON.parse(localStorage.getItem("user") || "null")?.roles ?? [];
  const technicianOnly = userRoles.length > 0 && userRoles.every((role) => role.name === "Technician");
  // Jobs (the tech app's Jobs tab) for anyone who works jobs as a technician.
  const hasTechnicianRole = userRoles.some((role) => role.name === "Technician");
  const chatUnread = useChatUnread();
  const jobsActive = location.pathname === "/jobs";

  // The MANAGEMENT header only shows when at least one of its items (Team,
  // Permissions, Reports, Forms) is visible — union of their gates below.
  const managementVisible = canAny([
    "user:read",
    "iam:readRole",
    "iam:readPolicy",
    "report:adHocRead",
    "report:scheduledRead",
    "ai:ask",
    "form:read",
    "form:templateRead",
  ]);

  // The bottom group (User Guides, Tickets, Platform) highlights in a different
  // colour to the main group. Listed explicitly rather than as ">= 9" so a new
  // main-group item can take the next free index without inheriting the bottom
  // colour by accident.
  const BOTTOM_ITEM_INDEXES = [9, 10, 11];

  const getBgColorArray = (activeIndex) => {
    const colors = Array(13).fill("bg-transparent");
    if (activeIndex >= 0) {
      const isBottomItem = BOTTOM_ITEM_INDEXES.includes(activeIndex);
      colors[activeIndex] = isBottomItem ? bottomHighlightColor : mainHighlightColor;
    }
    return colors;
  };

  const [bgColorArray, setBgColorArray] = useState(getBgColorArray(-1));
  const isActive = (index) => bgColorArray[index] !== "bg-transparent";
  const getTextColorClass = (index) => {
    return isActive(index) ? "text-white" : "text-black";
  };
  const getHoverClass = (index) => {
    return bgColorArray[index] === "bg-transparent" ? "hover:bg-menuHighlightColor" : "";
  };
  const getIcon = (index, whiteIcon, blackIcon) => {
    return getImageUrl(isActive(index) ? whiteIcon : blackIcon);
  };
  const formsActive = location.pathname.includes("/form-templates") || location.pathname.includes("/all-forms");
  // Requests is the Work Orders page filtered to customer requests (?requests=1).
  const requestsActive =
    location.pathname === "/work" && new URLSearchParams(location.search).get("requests") === "1";

  useEffect(() => {
    if (requestsActive) {
      setBgColorArray(getBgColorArray(-1));
      setWorkOrdersExpanded(true);
    } else if (location.pathname.includes("/work")) {
      setBgColorArray(getBgColorArray(0));
    } else if (location.pathname.includes("/purchase")) {
      setBgColorArray(getBgColorArray(1));
    } else if (location.pathname.includes("/recommendations")) {
      setBgColorArray(getBgColorArray(2));
    } else if (location.pathname.includes("/customer")) {
      setBgColorArray(getBgColorArray(3));
    } else if (location.pathname.includes("/calendar")) {
      setBgColorArray(getBgColorArray(4));
    } else if (location.pathname.includes("/my-time-off")) {
      setBgColorArray(getBgColorArray(6));
    } else if (location.pathname.includes("/time-management")) {
      setBgColorArray(getBgColorArray(12));
    } else if (location.pathname.includes("/time-off")) {
      setBgColorArray(getBgColorArray(5));
    } else if (location.pathname.includes("/team")) {
      setBgColorArray(getBgColorArray(7));
    } else if (location.pathname.includes("/report")) {
      setBgColorArray(getBgColorArray(8));
    } else if (location.pathname.includes("/form-templates") || location.pathname.includes("/all-forms")) {
      setBgColorArray(getBgColorArray(-1));
      setFormsExpanded(true);
    } else if (location.pathname.startsWith("/roles") || location.pathname.startsWith("/policies")) {
      setBgColorArray(getBgColorArray(-1));
      setPermissionsExpanded(true);
    } else if (location.pathname.includes("/userguides")) {
      setBgColorArray(getBgColorArray(9));
    } else if (location.pathname.includes("/ticket")) {
      setBgColorArray(getBgColorArray(10));
    } else if (location.pathname.includes("/companies") || location.pathname.includes("/platform")) {
      setBgColorArray(getBgColorArray(11));
    } else {
      setBgColorArray(getBgColorArray(-1));
    }
  }, [location.pathname, requestsActive]);

  // Switching between Work Orders and Requests keeps the selected branch tab.
  // The branch bar only fills the branch in when the page first loads, so
  // without it the list would ask for no branch and come back empty.
  const workListUrl = (requests) => {
    const params = new URLSearchParams();
    if (requests) params.set("requests", "1");
    const branch = location.pathname === "/work" ? new URLSearchParams(location.search).get("branch") : null;
    if (branch !== null) params.set("branch", branch);
    const query = params.toString();
    return query ? `/work?${query}` : "/work";
  };

  const workOrderClicked = () => {
    if (!location.pathname.includes("/work") || requestsActive) {
      navigate(workListUrl(false));
      setBgColorArray(getBgColorArray(0));
    }
  };

  const workOrdersToggle = (e) => {
    // The chevron opens/closes the list (the row itself opens Work Orders).
    // Closing it while on Requests goes back to the full Work Orders list.
    e.stopPropagation();
    if (workOrdersExpanded && requestsActive) {
      navigate(workListUrl(false));
    }
    setWorkOrdersExpanded((prev) => !prev);
  };

  const requestsClicked = () => {
    if (!requestsActive) {
      navigate(workListUrl(true));
      setBgColorArray(getBgColorArray(-1));
    }
  };

  const purchaseOrderClicked = () => {
    if (!location.pathname.includes("/purchase")) {
      navigate("/purchase");
      setBgColorArray(getBgColorArray(1));
    }
  };

  const recommendationsClicked = () => {
    navigate("/recommendations");
    setBgColorArray(getBgColorArray(2));
  };

  const customerClicked = () => {
    navigate("/customer");
    setBgColorArray(getBgColorArray(3));
  };

  const calendarClicked = () => {
    navigate("/calendar");
    setBgColorArray(getBgColorArray(4));
  };

  const reportCenterClicked = () => {
    navigate("/report/ad-hoc");
    setBgColorArray(getBgColorArray(8));
  };

  const teamClicked = () => {
    navigate("/team/manage");
    setBgColorArray(getBgColorArray(7));
  };

  const timeOffClicked = () => {
    navigate("/time-off");
    setBgColorArray(getBgColorArray(5));
  };

  const myTimeOffClicked = () => {
    navigate("/my-time-off");
    setBgColorArray(getBgColorArray(6));
  };

  const timeManagementClicked = () => {
    navigate("/time-management");
    setBgColorArray(getBgColorArray(12));
  };

  const rolesClicked = () => {
    navigate("/roles");
    setBgColorArray(getBgColorArray(-1));
  };

  const policiesClicked = () => {
    navigate("/policies");
    setBgColorArray(getBgColorArray(-1));
  };

  const rolesActive = location.pathname.startsWith("/roles");
  const policiesActive = location.pathname.startsWith("/policies");
  const permissionsActive = rolesActive || policiesActive;
  const permissionsToggle = () => setPermissionsExpanded((prev) => !prev);

  const formsToggle = () => {
    setFormsExpanded((prev) => !prev);
  };

  const templatesClicked = () => {
    navigate("/form-templates");
    setBgColorArray(getBgColorArray(-1));
  };

  const allFormsClicked = () => {
    navigate("/all-forms");
    setBgColorArray(getBgColorArray(-1));
  };

  const userGuidesClicked = () => {
    navigate("/userguides");
    setBgColorArray(getBgColorArray(9));
  };

  const ticketClicked = () => {
    navigate("/ticket");
    setBgColorArray(getBgColorArray(10));
  };

  const reportBugClicked = () => {
    navigate("/ticket/new");
    setBgColorArray(getBgColorArray(10));
  };

  const tenantsClicked = () => {
    navigate("/platform/tenants");
    setBgColorArray(getBgColorArray(11));
  };

  const exitTenant = () => {
    clearActingTenant();
    // Full reload for the same reason as entering a tenant (see enterTenant in
    // CompanyManagement): drop the tenant's vocabulary, permissions and cached
    // data and return cleanly to the Super Admin's own context.
    window.location.assign("/platform/tenants");
  };

  // Platform view for a Super Admin who hasn't entered a tenant: only the
  // cross-tenant items (Tenants, Ticket Management) are available. Both are
  // primary items here, so the active one gets the main blue highlight (the
  // shared bgColorArray would paint them with the bottom-item color).
  if (isPlatformMode) {
    // /platform/ticket also starts with /platform, so match Tenants precisely.
    const tenantsActive = location.pathname.includes("/platform/tenants") || location.pathname.includes("/companies");
    const ticketActive = location.pathname.includes("/ticket");
    const appVersionsActive = location.pathname.includes("/platform/app-versions");
    return (
      <div className="w-[257px] h-screen fixed top-0 left-0 bg-backgroundColor border-r-[1px] border-borderColor overflow-y-auto flex flex-col">
        <div className="w-full h-[80px] flex items-center px-[30px] mt-7">
          <img className="aspect-auto w-[190px]" src={getImageUrl("/images/logos/Black logo - no background.png")} />
        </div>

        <div className="w-full flex flex-1 pl-5 pr-1 flex-col mt-5 gap-1">
          <div className="px-[12px] mb-1 text-[#868DA6] font-OpenSansRegular text-[12px] not-italic font-[600] leading-[18px] tracking-[0.12px] uppercase">
            PLATFORM
          </div>

          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${
              tenantsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"
            }`}
            onClick={tenantsClicked}
          >
            <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/hotel.svg")} />
            <div
              className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${
                tenantsActive ? "text-white" : "text-black"
              }`}
            >
              Tenants
            </div>
          </div>

          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${
              ticketActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"
            }`}
            onClick={() => navigate("/platform/ticket")}
          >
            <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/help/info.png")} />
            <div
              className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${
                ticketActive ? "text-white" : "text-black"
              }`}
            >
              Ticket Management
            </div>
            {hasUnreadTicket && <span className="ml-auto w-[8px] h-[8px] rounded-full bg-red-500 shrink-0" />}
          </div>

          {/* <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${
              appVersionsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"
            }`}
            onClick={() => navigate("/platform/app-versions")}
          >
            <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/mobile.svg")} />
            <div
              className={`text-inputEmailTitle text-[16px] font-OpenSansSemiBold ${
                appVersionsActive ? "text-white" : "text-black"
              }`}
            >
              App Versions
            </div>
          </div> */}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[257px] h-screen fixed top-0 left-0 bg-backgroundColor border-r-[1px] border-borderColor overflow-y-auto flex flex-col">
      <div className="w-full h-[80px] flex items-center px-[30px] mt-7">
        <img className="aspect-auto w-[190px]" src={getImageUrl("/images/logos/Black logo - no background.png")} />
      </div>

      {/* Super Admin acting inside a tenant: show which one, with a way back
          to the Platform view. */}
      {actingTenant && (
        <div className="mx-5 mt-4 px-3 py-2 rounded-[8px] border-[1px] border-blueColor bg-white flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-OpenSansRegular font-[600] text-subTitleColor uppercase">Tenant</p>
            <p className="text-[13px] font-OpenSansSemiBold text-titleColor truncate">
              {actingTenant.name || actingTenant.identifier}
            </p>
          </div>
          <span
            onClick={exitTenant}
            className="text-[11px] font-OpenSansSemiBold text-blueColor cursor-pointer shrink-0"
          >
            EXIT
          </span>
        </div>
      )}

      <div className="w-full flex flex-1 pl-5 pr-1 flex-col mt-5 gap-1">
        {can("wo:read") && hasTechnicianRole && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${
              jobsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"
            }`}
            onClick={() => !jobsActive && navigate("/jobs")}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getImageUrl(jobsActive ? "/images/sidemenu/white_lists.png" : "/images/sidemenu/black_lists.png")}
            />
            <div
              className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${
                jobsActive ? "text-white" : "text-black"
              }`}
            >
              Jobs
            </div>
            {/* Unread customer messages on their jobs. */}
            {technicianOnly && <CountBadge count={chatUnread.total} />}
          </div>
        )}
        {can("wo:read") && (
          <div>
            <div
              className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(0)} ${bgColorArray[0]}`}
              onClick={workOrderClicked}
            >
              <img
                className="aspect-auto w-[18px]"
                src={getIcon(0, "/images/sidemenu/white_wo.png", "/images/sidemenu/black_wo.png")}
              />
              <div
                className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${getTextColorClass(0)}`}
              >
                {termPlural("workOrder")}
              </div>
              {/* Collapsed: everything. Expanded: what isn't under Customer
                  Requests, i.e. work orders with unread customer messages. */}
              <CountBadge
                count={
                  technicianOnly
                    ? 0
                    : workOrdersExpanded
                      ? Math.max(0, pendingRequestCount.all - pendingRequestCount.requests)
                      : pendingRequestCount.all
                }
              />
              {!technicianOnly && (
              <button
                type="button"
                aria-label={workOrdersExpanded ? "Hide work order views" : "Show work order views"}
                aria-expanded={workOrdersExpanded}
                onClick={workOrdersToggle}
                className="w-[28px] h-[28px] -mr-[6px] flex items-center justify-center rounded-full cursor-pointer hover:bg-black/10"
              >
                <svg
                  className={`w-[14px] h-[14px] transition-transform ${workOrdersExpanded ? "" : "rotate-180"}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={isActive(0) ? "#ffffff" : "#000000"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
              )}
            </div>
            {workOrdersExpanded && !technicianOnly && (
              <div className="flex flex-col gap-1 mt-1 ml-[26px]">
                <div
                  className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${requestsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                  onClick={requestsClicked}
                >
                  <div
                    className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${requestsActive ? "text-white" : "text-black"}`}
                  >
                    Customer Requests
                  </div>
                  <CountBadge count={pendingRequestCount.requests} />
                </div>
              </div>
            )}
          </div>
        )}

        {can("po:read") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(1)} ${bgColorArray[1]}`}
            onClick={purchaseOrderClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(1, "/images/sidemenu/white_po.png", "/images/sidemenu/black_po.svg")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(1)}`}>
              Purchase Orders
            </div>
          </div>
        )}

        {can("recommendation:read") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(2)} ${bgColorArray[2]}`}
            onClick={recommendationsClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(
                2,
                "/images/sidemenu/white_recommendation.png",
                "/images/sidemenu/black_recommendations.png",
              )}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(2)}`}>
              {termPlural("recommendation")}
            </div>
          </div>
        )}

        {can("customer:read") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(3)} ${bgColorArray[3]}`}
            onClick={customerClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(3, "/images/sidemenu/white_customer.png", "/images/sidemenu/black_customer.png")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(3)}`}>
              Customers
            </div>
          </div>
        )}
        {/* Calendar is visible when at least one of its tabs is
            (Work Orders → wo:read, Technicians/Time Off → time-off:manage). */}
        {canAny(["wo:read", "time-off:manage"]) && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(4)} ${bgColorArray[4]}`}
            onClick={calendarClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(4, "/images/sidemenu/white_date.png", "/images/sidemenu/black_date.png")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(4)}`}>
              Calendar
            </div>
          </div>
        )}

        {/* Time Management: read-only payroll view of hours logged per
            technician. Gated on its own permission rather than the work order
            timesheet's — this is a payroll screen, and who reads payroll is a
            separate decision from who reads a WO's hours. */}
        {can("time-management:read") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(12)} ${bgColorArray[12]}`}
            onClick={timeManagementClicked}
          >
            <svg
              className="aspect-auto w-[18px]"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <circle cx="9" cy="9" r="7.25" stroke={isActive(12) ? "#ffffff" : "#000000"} strokeWidth="1.5" />
              <path
                d="M9 4.75V9l2.75 1.75"
                stroke={isActive(12) ? "#ffffff" : "#000000"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(12)}`}>
              Time Management
            </div>
          </div>
        )}

        {/* Time Off Requests: the admin approval queue (time-off:manage). */}
        {can("time-off:manage") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(5)} ${bgColorArray[5]}`}
            onClick={timeOffClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(5, "/images/sidemenu/white_timeoff.svg", "/images/sidemenu/black_timeoff.svg")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(5)}`}>
              Time Off Requests
            </div>
          </div>
        )}

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(6)} ${bgColorArray[6]}`}
          onClick={myTimeOffClicked}
        >
          <svg
            className="aspect-auto w-[18px]"
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.16667 3.95833V0.625C4.16667 0.279822 4.44649 0 4.79167 0C5.13684 0 5.41667 0.279822 5.41667 0.625V3.95833C5.41667 4.30351 5.13684 4.58333 4.79167 4.58333C4.44649 4.58333 4.16667 4.30351 4.16667 3.95833ZM10.8333 3.95833V0.625C10.8333 0.279822 11.1132 0 11.4583 0C11.8035 0 12.0833 0.279822 12.0833 0.625V3.95833C12.0833 4.30351 11.8035 4.58333 11.4583 4.58333C11.1132 4.58333 10.8333 4.30351 10.8333 3.95833ZM15 3.95833C15 3.38304 14.5336 2.91667 13.9583 2.91667H2.29167C1.71637 2.91667 1.25 3.38304 1.25 3.95833V15.625C1.25 16.2003 1.71637 16.6667 2.29167 16.6667H13.9583C14.5336 16.6667 15 16.2003 15 15.625V3.95833ZM16.25 15.625C16.25 16.8907 15.224 17.9167 13.9583 17.9167H2.29167C1.02601 17.9167 0 16.8907 0 15.625V3.95833C0 2.69268 1.02601 1.66667 2.29167 1.66667H13.9583C15.224 1.66667 16.25 2.69268 16.25 3.95833V15.625ZM15.625 6.66667C15.9702 6.66667 16.25 6.94649 16.25 7.29167C16.25 7.63684 15.9702 7.91667 15.625 7.91667H0.625C0.279822 7.91667 0 7.63684 0 7.29167C0 6.94649 0.279822 6.66667 0.625 6.66667H15.625ZM10.1831 10.1831C10.4272 9.93903 10.8228 9.93903 11.0669 10.1831C11.311 10.4272 11.311 10.8228 11.0669 11.0669L7.73356 14.4002C7.48948 14.6443 7.09385 14.6443 6.84977 14.4002L5.1831 12.7336C4.93903 12.4895 4.93903 12.0938 5.1831 11.8498C5.42718 11.6057 5.82282 11.6057 6.06689 11.8498L7.29167 13.0745L10.1831 10.1831Z"
              fill={isActive(6) ? "#FFFFFF" : "#1A2332"}
            />
          </svg>
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(6)}`}>
            My Time Off
          </div>
        </div>

        {managementVisible && (
          <div className="px-[12px] mt-3 mb-1 text-[#868DA6] font-OpenSansRegular text-[12px] not-italic font-[600] leading-[18px] tracking-[0.12px] uppercase">
            MANAGEMENT
          </div>
        )}

        {can("user:read") && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(7)} ${bgColorArray[7]}`}
            onClick={teamClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(7, "/images/sidemenu/white_team.svg", "/images/sidemenu/black_team.svg")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(7)}`}>
              Team
            </div>
          </div>
        )}

        {canAny(["iam:readRole", "iam:readPolicy"]) && (
          <div>
            <div
              className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${permissionsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
              onClick={permissionsToggle}
            >
              <img
                className="aspect-auto w-[18px]"
                src={getImageUrl(
                  permissionsActive ? "/images/sidemenu/white_lists.png" : "/images/sidemenu/black_lists.png",
                )}
              />
              <div
                className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${permissionsActive ? "text-white" : "text-black"}`}
              >
                Permissions
              </div>
              <svg
                className={`w-[14px] h-[14px] transition-transform ${permissionsExpanded ? "" : "rotate-180"}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke={permissionsActive ? "#ffffff" : "#000000"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </div>
            {permissionsExpanded && (
              <div className="flex flex-col gap-1 mt-1 ml-[26px]">
                {can("iam:readRole") && (
                  <div
                    className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${rolesActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                    onClick={rolesClicked}
                  >
                    <div
                      className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${rolesActive ? "text-white" : "text-black"}`}
                    >
                      Roles
                    </div>
                  </div>
                )}
                {can("iam:readPolicy") && (
                  <div
                    className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${policiesActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                    onClick={policiesClicked}
                  >
                    <div
                      className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${policiesActive ? "text-white" : "text-black"}`}
                    >
                      Policies
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {canAny(["report:adHocRead", "report:scheduledRead", "ai:ask"]) && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(8)} ${bgColorArray[8]}`}
            onClick={reportCenterClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(8, "/images/sidemenu/white_report.svg", "/images/sidemenu/black_report.svg")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(8)}`}>
              Reports
            </div>
          </div>
        )}

        {canAny(["form:read", "form:templateRead"]) && (
          <div>
            <div
              className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${formsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
              onClick={formsToggle}
            >
              <img
                className="aspect-auto w-[18px]"
                src={getImageUrl(formsActive ? "/images/sidemenu/white_lists.png" : "/images/sidemenu/black_lists.png")}
              />
              <div
                className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${formsActive ? "text-white" : "text-black"}`}
              >
                Forms
              </div>
              <svg
                className={`w-[14px] h-[14px] transition-transform ${formsExpanded ? "" : "rotate-180"}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke={formsActive ? "#ffffff" : "#000000"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </div>
            {formsExpanded && (
              <div className="flex flex-col gap-1 mt-1 ml-[26px]">
                {can("form:templateRead") && (
                  <div
                    className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${location.pathname.includes("/form-templates") ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                    onClick={templatesClicked}
                  >
                    <div
                      className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${location.pathname.includes("/form-templates") ? "text-white" : "text-black"}`}
                    >
                      Templates
                    </div>
                  </div>
                )}
                {can("form:read") && (
                  <div
                    className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${location.pathname.includes("/all-forms") ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                    onClick={allFormsClicked}
                  >
                    <div
                      className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${location.pathname.includes("/all-forms") ? "text-white" : "text-black"}`}
                    >
                      All Forms
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col pl-5 pr-1 pb-6 gap-1 mt-auto">
        {/* <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${bgColorArray[5]}`}
          onClick={userGuidesClicked}
        >
          <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/help/video.png")} />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(9)}`}>
            User Guides
          </div>
        </div> */}
        {/* Hidden while a Super Admin is acting inside a tenant — Ticket
            Management belongs to the Platform view. */}
        {!actingTenant && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(10)} ${bgColorArray[10]}`}
            onClick={ticketClicked}
          >
            <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/help/info.png")} />
            <div
              className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(10)}`}
            >
              {isSuperAdmin ? "Ticket Management" : "Report Bug"}
            </div>
            {/* Red dot indicator: visible when the user has at least one unread ticket */}
            {hasUnreadTicket && <span className="ml-auto w-[8px] h-[8px] rounded-full bg-red-500 shrink-0" />}
          </div>
        )}
      </div>
    </div>
  );
}

export default SideMenu;
